import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { authService } from '../../services/authService'
import { resolverErrorApi } from '../../services/apiError'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { motion } from 'framer-motion'
import {
  Truck,
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  AlertCircle,
  Loader2,
  CheckCircle2,
  Sparkles,
  KeyRound,
  Send,
} from 'lucide-react'

const schemaSolicitar = z.object({
  email: z
    .string({ required_error: 'Ingresa tu correo electrónico' })
    .email('Ingresa un correo electrónico válido'),
})

const schemaConfirmar = z
  .object({
    newPassword: z
      .string({ required_error: 'Ingresa la nueva contraseña' })
      .min(6, 'La contraseña debe tener al menos 6 caracteres'),
    confirmPassword: z.string({ required_error: 'Confirma la nueva contraseña' }),
  })
  .superRefine((data, ctx) => {
    if (data.newPassword !== data.confirmPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Las contraseñas no coinciden',
        path: ['confirmPassword'],
      })
    }
  })

type DatosSolicitar = z.infer<typeof schemaSolicitar>
type DatosConfirmar = z.infer<typeof schemaConfirmar>

export const PaginaResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const tokenDesdeUrl = searchParams.get('token')
  const emailDesdeUrl = searchParams.get('email') ?? ''
  const modoConfirmar = !!tokenDesdeUrl

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [generalError, setGeneralError] = useState<string | null>(null)
  const [mensajeExito, setMensajeExito] = useState<string | null>(null)
  const [emailEnviado, setEmailEnviado] = useState<{ email: string; detalle?: string } | null>(null)

  const formSolicitar = useForm<DatosSolicitar>({
    resolver: zodResolver(schemaSolicitar),
    defaultValues: { email: emailDesdeUrl },
  })

  const formConfirmar = useForm<DatosConfirmar>({
    resolver: zodResolver(schemaConfirmar),
    defaultValues: { newPassword: '', confirmPassword: '' },
  })

  useEffect(() => {
    if (emailDesdeUrl && !modoConfirmar) {
      formSolicitar.setValue('email', emailDesdeUrl)
    }
  }, [emailDesdeUrl, modoConfirmar, formSolicitar])

  const onSubmitSolicitar = async (data: DatosSolicitar) => {
    setGeneralError(null)
    setMensajeExito(null)
    setEmailEnviado(null)
    try {
      const respuesta = await authService.requestPasswordReset(data.email.trim().toLowerCase())
      setEmailEnviado({ email: data.email, detalle: respuesta.email_detalle })
      setMensajeExito(respuesta.message || 'Solicitud enviada correctamente.')
    } catch (error: unknown) {
      const info = resolverErrorApi(error, 'No se pudo procesar la solicitud. Inténtalo de nuevo.')
      setGeneralError(info.message)
    }
  }

  const onSubmitConfirmar = async (data: DatosConfirmar) => {
    setGeneralError(null)
    setMensajeExito(null)
    if (!tokenDesdeUrl) {
      setGeneralError('No hay un token de restablecimiento válido en la URL.')
      return
    }
    try {
      const respuesta = await authService.confirmPasswordReset(
        tokenDesdeUrl,
        data.newPassword,
        data.confirmPassword,
      )
      setMensajeExito(respuesta.message || 'Contraseña actualizada correctamente.')
      setTimeout(() => navigate('/login'), 2500)
    } catch (error: unknown) {
      const info = resolverErrorApi(error, 'No se pudo actualizar la contraseña. Inténtalo de nuevo.')
      setGeneralError(info.message)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-[#030303] dark:text-zinc-100 relative flex items-center justify-center overflow-hidden selection:bg-cyan-500/30">

      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] dark:bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)]" />
        <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[40%] md:w-[40%] md:h-[40%] rounded-full bg-cyan-500/20 blur-[100px] md:blur-[120px] mix-blend-screen dark:bg-cyan-500/10" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[40%] md:w-[40%] md:h-[40%] rounded-full bg-violet-500/20 blur-[100px] md:blur-[120px] mix-blend-screen dark:bg-violet-500/10" />
      </div>

      <header className="absolute top-6 left-0 right-0 z-50 w-full max-w-7xl mx-auto flex justify-between px-6 md:px-12">
        <Link
          to="/login"
          className="group flex items-center gap-2 rounded-full border border-slate-200/50 bg-white/70 px-4 py-2 backdrop-blur-xl shadow-sm dark:border-white/10 dark:bg-black/50 transition-all hover:bg-white dark:hover:bg-black"
        >
          <ArrowLeft className="h-4 w-4 text-slate-500 transition-transform group-hover:-translate-x-1 dark:text-zinc-400" />
          <span className="text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-zinc-300">
            Volver al inicio de sesión
          </span>
        </Link>
      </header>

      <div className="relative z-10 w-full max-w-3xl mx-auto grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 px-6 md:px-12 py-24">

        <div className="hidden lg:flex flex-col justify-center">
          <Link to="/" className="flex items-center gap-3 mb-10">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 text-white shadow-lg shadow-cyan-500/25">
              <Truck className="h-5 w-5" />
            </div>
            <span className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">
              LogiXport
            </span>
          </Link>

          <div className="max-w-md space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/20 bg-cyan-500/10 px-3 py-1.5 text-[10px] md:text-xs font-bold tracking-widest text-cyan-700 dark:border-cyan-500/15 dark:bg-cyan-500/10 dark:text-cyan-400 mb-6">
                <KeyRound className="h-3 w-3 md:h-4 md:w-4" />
                <span>{modoConfirmar ? 'Restablecer contraseña' : 'Recupera tu acceso'}</span>
              </div>

              <h1 className="text-4xl lg:text-5xl font-extrabold tracking-tighter text-slate-900 dark:text-white leading-[1.1] mb-5">
                {modoConfirmar ? (
                  <>
                    Elige tu nueva <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-violet-500">
                      contraseña
                    </span>
                  </>
                ) : (
                  <>
                    ¿Olvidaste tu <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-violet-500">
                      contraseña?
                    </span>
                  </>
                )}
              </h1>
              <p className="text-base lg:text-lg text-slate-600 dark:text-zinc-400 leading-relaxed font-medium">
                {modoConfirmar
                  ? 'Crea una contraseña segura para volver a ingresar a tu panel de LogiXport.'
                  : 'Te enviaremos un enlace seguro a tu correo. Síguelo para restablecer el acceso en menos de 1 minuto.'}
              </p>
            </motion.div>

            <ul className="space-y-3">
              {[
                modoConfirmar ? 'Mínimo 6 caracteres' : 'Token válido por 60 minutos',
                modoConfirmar ? 'Puedes usar mayúsculas, números y símbolos' : 'Solo 1 click para continuar',
                modoConfirmar ? 'Cambio aplicado inmediatamente' : 'Sin necesidad de contactar soporte',
              ].map((item, idx) => (
                <motion.li
                  key={item}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 + idx * 0.1 }}
                  className="flex items-center gap-3 text-sm text-slate-700 dark:text-zinc-300"
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-cyan-500/15">
                    <CheckCircle2 className="h-3.5 w-3.5 text-cyan-500" />
                  </span>
                  {item}
                </motion.li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-md"
          >
            <div className="rounded-[2rem] border border-slate-200/50 bg-white/70 p-6 sm:p-8 shadow-2xl backdrop-blur-xl dark:border-white/10 dark:bg-white/5">

              <div className="text-center mb-6">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-600 text-white shadow-lg">
                  {modoConfirmar ? <Lock className="h-5 w-5" /> : <Send className="h-5 w-5" />}
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white tracking-tight mb-2">
                  {modoConfirmar ? 'Nueva contraseña' : 'Recuperar acceso'}
                </h2>
                <p className="text-sm text-slate-600 dark:text-zinc-400">
                  {modoConfirmar
                    ? emailDesdeUrl
                      ? `Para: <span className="font-semibold text-slate-800 dark:text-zinc-200">${emailDesdeUrl}</span>`
                      : 'Ingresa tu nueva contraseña'
                    : 'Envía el enlace de recuperación a tu correo'}
                </p>
              </div>

              {!modoConfirmar ? (
                <form onSubmit={formSolicitar.handleSubmit(onSubmitSolicitar)} className="space-y-4">

                  <div className="space-y-1.5">
                    <label htmlFor="email_reset" className="block text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-zinc-300">
                      Correo electrónico
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Mail className="h-4 w-4 text-slate-400 dark:text-zinc-500" />
                      </div>
                      <input
                        type="email"
                        id="email_reset"
                        {...formSolicitar.register('email')}
                        className={`block w-full rounded-xl border bg-white/50 py-3 pl-11 pr-4 text-sm text-slate-900 placeholder-slate-400 backdrop-blur-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 dark:bg-black/50 dark:text-white dark:placeholder-zinc-600 ${
                          formSolicitar.formState.errors.email ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500' : 'border-slate-200/50 dark:border-white/10'
                        } ${mensajeExito ? 'border-emerald-500/40 focus:ring-emerald-500 focus:border-emerald-500' : ''}`}
                        placeholder="tu@email.com"
                        disabled={formSolicitar.formState.isSubmitting || !!mensajeExito}
                      />
                    </div>
                    {formSolicitar.formState.errors.email?.message && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> {formSolicitar.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  {generalError && !mensajeExito && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="overflow-hidden"
                    >
                      <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-center text-xs font-medium text-red-600 dark:text-red-400 flex items-center justify-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        {generalError}
                      </div>
                    </motion.div>
                  )}

                  {mensajeExito && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="overflow-hidden space-y-2"
                    >
                      <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-3 text-center text-xs font-medium text-emerald-700 dark:text-emerald-400 flex items-center justify-center gap-2">
                        <CheckCircle2 className="h-4 w-4" />
                        {mensajeExito}
                      </div>
                      {emailEnviado?.detalle && (
                        <div className="rounded-xl border border-slate-200/60 bg-slate-50 dark:border-white/10 dark:bg-white/5 p-3 text-[11px] text-slate-600 dark:text-zinc-400 leading-relaxed">
                          <span className="font-bold text-slate-800 dark:text-zinc-200 inline-flex items-center gap-1 mb-1">
                            <Sparkles className="h-3 w-3" /> Detalle:
                          </span>
                          <br />
                          {emailEnviado.detalle}
                        </div>
                      )}
                    </motion.div>
                  )}

                  <button
                    type="submit"
                    disabled={formSolicitar.formState.isSubmitting || !!mensajeExito}
                    className="group relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-8 py-3.5 text-sm font-bold uppercase tracking-widest text-white transition-all hover:scale-[1.02] hover:shadow-[0_0_40px_-10px_rgba(6,182,212,0.6)] disabled:opacity-70 disabled:hover:scale-100"
                  >
                    {formSolicitar.formState.isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Enviando...</span>
                      </>
                    ) : mensajeExito ? (
                      <span>Reenviar enlace</span>
                    ) : (
                      <span>Enviar enlace de recuperación</span>
                    )}
                  </button>

                  <div className="relative flex items-center justify-center py-3">
                    <div className="w-full border-t border-slate-200/50 dark:border-white/10" />
                    <span className="absolute bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:bg-[#0a0a0a] dark:text-zinc-500">
                      ¿Ya recordaste?
                    </span>
                  </div>

                  <div className="text-center">
                    <Link
                      to="/login"
                      className="text-sm font-medium text-slate-600 dark:text-zinc-400 hover:text-cyan-500 dark:hover:text-cyan-400 transition-colors"
                    >
                      Inicia sesión en <span className="font-bold">LogiXport</span>
                    </Link>
                  </div>
                </form>
              ) : (
                <form onSubmit={formConfirmar.handleSubmit(onSubmitConfirmar)} className="space-y-4">

                  <div className="space-y-1.5">
                    <label htmlFor="new_password" className="block text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-zinc-300">
                      Nueva contraseña
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Lock className="h-4 w-4 text-slate-400 dark:text-zinc-500" />
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="new_password"
                        {...formConfirmar.register('newPassword')}
                        className={`block w-full rounded-xl border bg-white/50 py-3 pl-11 pr-11 text-sm text-slate-900 placeholder-slate-400 backdrop-blur-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 dark:bg-black/50 dark:text-white dark:placeholder-zinc-600 ${
                          formConfirmar.formState.errors.newPassword ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500' : 'border-slate-200/50 dark:border-white/10'
                        }`}
                        placeholder="••••••••"
                        disabled={formConfirmar.formState.isSubmitting || !!mensajeExito}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-cyan-500 dark:text-zinc-500 dark:hover:text-cyan-400 transition-colors"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={formConfirmar.formState.isSubmitting || !!mensajeExito}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {formConfirmar.formState.errors.newPassword?.message && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> {formConfirmar.formState.errors.newPassword.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <label htmlFor="confirm_new_password" className="block text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-zinc-300">
                      Confirmar nueva contraseña
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Lock className="h-4 w-4 text-slate-400 dark:text-zinc-500" />
                      </div>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirm_new_password"
                        {...formConfirmar.register('confirmPassword')}
                        className={`block w-full rounded-xl border bg-white/50 py-3 pl-11 pr-11 text-sm text-slate-900 placeholder-slate-400 backdrop-blur-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 dark:bg-black/50 dark:text-white dark:placeholder-zinc-600 ${
                          formConfirmar.formState.errors.confirmPassword ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500' : 'border-slate-200/50 dark:border-white/10'
                        }`}
                        placeholder="••••••••"
                        disabled={formConfirmar.formState.isSubmitting || !!mensajeExito}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-cyan-500 dark:text-zinc-500 dark:hover:text-cyan-400 transition-colors"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        disabled={formConfirmar.formState.isSubmitting || !!mensajeExito}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {formConfirmar.formState.errors.confirmPassword?.message && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> {formConfirmar.formState.errors.confirmPassword.message}
                      </p>
                    )}
                  </div>

                  {generalError && !mensajeExito && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="overflow-hidden"
                    >
                      <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-center text-xs font-medium text-red-600 dark:text-red-400 flex items-center justify-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        {generalError}
                      </div>
                    </motion.div>
                  )}

                  {mensajeExito && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="overflow-hidden"
                    >
                      <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-3 text-center text-xs font-medium text-emerald-700 dark:text-emerald-400 flex items-center justify-center gap-2">
                        <CheckCircle2 className="h-4 w-4" />
                        {mensajeExito}
                      </div>
                    </motion.div>
                  )}

                  <button
                    type="submit"
                    disabled={formConfirmar.formState.isSubmitting || !!mensajeExito}
                    className="group relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-8 py-3.5 text-sm font-bold uppercase tracking-widest text-white transition-all hover:scale-[1.02] hover:shadow-[0_0_40px_-10px_rgba(6,182,212,0.6)] disabled:opacity-70 disabled:hover:scale-100"
                  >
                    {formConfirmar.formState.isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Actualizando...</span>
                      </>
                    ) : (
                      <span>Actualizar contraseña</span>
                    )}
                  </button>

                  <div className="relative flex items-center justify-center py-3">
                    <div className="w-full border-t border-slate-200/50 dark:border-white/10" />
                    <span className="absolute bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:bg-[#0a0a0a] dark:text-zinc-500">
                      ¿Token expiró?
                    </span>
                  </div>

                  <div className="text-center">
                    <Link
                      to="/forgot-password"
                      className="text-sm font-medium text-slate-600 dark:text-zinc-400 hover:text-cyan-500 dark:hover:text-cyan-400 transition-colors"
                    >
                      Solicita un nuevo enlace
                    </Link>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default PaginaResetPassword
